import { AutogenLayoutAutoMatchProfile } from "./autogen/AutogenLayoutAutoMatchProfile";
import { Attachment, Button, ChangeEvent, Checkbox, ClickEvent, Component, DataDisplayEvent, DataHeader, DataSourceExecutionEvent, DataSourceMode, EditRowDecorator, Image, Label, Layout, Location, LookupModelSearchEvent, LookupModelSelectionEvent, Panel, PrintableEvent, Snackbar, Step, StepEvent, Switch, Table, TableRow, TableRowCreationEvent, TableRowDisplayEvent, Textbox, ValidationResult } from "@mcleod/components";
import { ActionPermission, ConversionUtil, ModelRow, ObjectUtil, PermissionsUtil } from "@mcleod/core";
import { Orders } from "./Orders";

type ConfigurableComponent = Textbox | Switch | Checkbox | Location;

export enum StartingStep {
    AutoFill = "auto-fill",
    RequiredEquipment = "required-equipment",
    Comments = "comments"
}

export class AutoMatchProfile extends AutogenLayoutAutoMatchProfile {
    private reviewAndCompleteHeadingComponents: Component[];
    private autoPopulatedFieldsStepHeadingComponents: Component[];
    private equipmentMatchesStepHeadingComponents: Component[];
    private commentsStepHeadingComponents: Component[];
    private equipmentTypeAppliesToFilter: string;
    private ordersLayout: Orders;
    public startingStep: StartingStep;

    override async onLoad(): Promise<void> {
        this.reviewAndCompleteHeadingComponents = this.stepReviewAndComplete.heading.components;
        this.autoPopulatedFieldsStepHeadingComponents = this.stepAutoPopulatedFields.heading.components;
        this.equipmentMatchesStepHeadingComponents = this.stepRequiredEquipment.heading.components;
        this.commentsStepHeadingComponents = this.stepComments.heading.components;
        this.setupQualifyingMatchListeners();
        this.adjustSteps();
        this.sourceEquipmentMatch.addAfterSearchPlusChildrenListener((event: DataSourceExecutionEvent) => {
            if (this.sourceEquipmentMatchDefault.data.length > 0) {
                this.buttonOrderFields.selected = true;
            }
            if (this.sourceEquipMatchDetail.data.length > 0) {
                this.buttonRequiredEquipment.selected = true;
            }
            if (this.sourceComments.data.length > 0) {
                this.buttonComments.selected = true;
            }
            this.adjustSteps();
            if (this.startingStep) {
                switch (this.startingStep) {
                    case StartingStep.AutoFill: {
                        this.stepAutoPopulatedFields.select();
                        break;
                    }
                    case StartingStep.RequiredEquipment: {
                        this.stepRequiredEquipment.select();
                        break;
                    }
                    case StartingStep.Comments: {
                        this.stepComments.select();
                        break;
                    }
                }
            }
        });

        const panelQualifierSummary = (this.layoutAutoMatchProfileQualifiersStep5?.findComponentById("panelQualifierSummary") as Panel);
        if (panelQualifierSummary != null) {
            panelQualifierSummary.padding = 0;
            panelQualifierSummary.borderWidth = 0;
            panelQualifierSummary.components.forEach((component) => {
                component.rowBreak = false;
                component.padding = 6;
                component.fillHeight = true;
                component.fillRow = false;
                component.minWidth = 105;
            });
        }
        const labelQualifiers = (this.layoutAutoMatchProfileQualifiersStep5?.findComponentById("labelQualifiers") as Textbox);
        if (labelQualifiers != null) {
            labelQualifiers.fontSize = "xlarge";
        }

        this.ordersLayout = Layout.getLayout("lme/dispatch/Orders") as Orders;
        await this.ordersLayout.ensureLoaded();
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.showDelete = true;
    }

    adjustSteps() {
        for (let i = this.stepperAutoMatchProfile.stepCount - 1; i > 0; i--) {
            this.stepperAutoMatchProfile.removeAt(i);
        }
        if (this.buttonOrderFields.selected) {
            this.stepAutoPopulatedFields.heading.addFromArray(this.autoPopulatedFieldsStepHeadingComponents);
            this.stepperAutoMatchProfile.add(this.stepAutoPopulatedFields);
        }
        if (this.buttonRequiredEquipment.selected) {
            this.stepRequiredEquipment.heading.addFromArray(this.equipmentMatchesStepHeadingComponents);
            this.stepperAutoMatchProfile.add(this.stepRequiredEquipment);
        }
        if (this.buttonComments.selected) {
            this.stepComments.heading.addFromArray(this.commentsStepHeadingComponents);
            this.stepperAutoMatchProfile.add(this.stepComments);
        }
        this.stepReviewAndComplete.heading.addFromArray(this.reviewAndCompleteHeadingComponents);
        this.stepperAutoMatchProfile.add(this.stepReviewAndComplete);
        this.stepperAutoMatchProfile.components.forEach((component: Step) => component.visited = true); // give the user the ability to jump to any step
    }

    buttonOrderFieldsOnClick(event: ClickEvent) {
        if (!this.buttonOrderFields.selected && this.sourceEquipmentMatchDefault.data.length > 0) {
            this.buttonOrderFields.selected = true;
            Snackbar.showSnackbar(`All auto-filled values from the "Set Auto-filled fields" step must be removed before this can be deselected.`);
            event.preventDefault();
            return;
        }
        this.adjustSteps();
        this.buttonOrderFields.scrollIntoView();
    }

    buttonRequiredEquipmentOnClick(event: ClickEvent) {
        if (!this.buttonRequiredEquipment.selected && this.sourceEquipMatchDetail.data.length > 0) {
            this.buttonRequiredEquipment.selected = true;
            Snackbar.showSnackbar(`All load requirements from the "Set Load Requirements" step must be removed before this can be deselected.`);
            event.preventDefault();
            return;
        }
        this.adjustSteps();
        this.buttonRequiredEquipment.scrollIntoView();
    }

    buttonCommentsOnClick(event: ClickEvent) {
        if (!this.buttonComments.selected && this.sourceComments.data.length > 0) {
            this.buttonComments.selected = true;
            Snackbar.showSnackbar(`All comments from the "Add Comments" step must be removed before this can be deselected.`);
            event.preventDefault();
            return;
        }
        this.adjustSteps();
        this.buttonComments.scrollIntoView();
    }

    private toggleCheckStyling(checkImage: Image, enable: boolean) {
        checkImage.color = enable ? "primary" : "subtle.light";
    }

    private setupQualifyingMatchListeners() {
        this.textboxCustomerId.addChangeListener((event: ChangeEvent) => this.qualifierFieldChange(event, this.imageCustomerCheck));
        this.textboxCustomerId.addDataDisplayListener((event: DataDisplayEvent) => this.qualifierFieldChange(event, this.imageCustomerCheck));
        this.textboxOrderMode.addChangeListener((event: ChangeEvent) => this.qualifierFieldChange(event, this.imageOrderModeCheck));
        this.textboxOrderMode.addDataDisplayListener((event: DataDisplayEvent) => this.qualifierFieldChange(event, this.imageOrderModeCheck));
        this.textboxRevenueCodeId.addChangeListener((event: ChangeEvent) => this.qualifierFieldChange(event, this.imageRevenueCodeCheck));
        this.textboxRevenueCodeId.addDataDisplayListener((event: DataDisplayEvent) => this.qualifierFieldChange(event, this.imageRevenueCodeCheck));
        this.textboxOrderTypeId.addChangeListener((event: ChangeEvent) => this.qualifierFieldChange(event, this.imageOrderTypeCheck));
        this.textboxOrderTypeId.addDataDisplayListener((event: DataDisplayEvent) => this.qualifierFieldChange(event, this.imageOrderTypeCheck));
        this.textboxTrailerMatch.addChangeListener((event: ChangeEvent) => this.qualifierFieldChange(event, this.imageTrailerTypeCheck));
        this.textboxTrailerMatch.addDataDisplayListener((event: DataDisplayEvent) => this.qualifierFieldChange(event, this.imageTrailerTypeCheck));
        this.textboxCommodityId.addChangeListener((event: ChangeEvent) => this.qualifierFieldChange(event, this.imageCommodityCheck));
        this.textboxCommodityId.addDataDisplayListener((event: DataDisplayEvent) => this.qualifierFieldChange(event, this.imageCommodityCheck));
        this.textboxLocationId.addChangeListener((event: ChangeEvent) => this.qualifierFieldChange(event, this.imageLocationCheck));
        this.textboxLocationId.addDataDisplayListener((event: DataDisplayEvent) => this.qualifierFieldChange(event, this.imageLocationCheck));
        this.textboxShipperLocationId.addChangeListener((event: ChangeEvent) => this.qualifierFieldChange(event, this.imageShipperCheck));
        this.textboxShipperLocationId.addDataDisplayListener((event: DataDisplayEvent) => this.qualifierFieldChange(event, this.imageShipperCheck));
        this.textboxConsigneeLocationId.addChangeListener((event: ChangeEvent) => this.qualifierFieldChange(event, this.imageConsigneeCheck));
        this.textboxConsigneeLocationId.addDataDisplayListener((event: DataDisplayEvent) => this.qualifierFieldChange(event, this.imageConsigneeCheck));
    }

    qualifierFieldChange(event, checkImage: Image) {
        const textbox = event.target as Textbox;
        const lookupModelData = textbox.getFirstLookupModelData() as ModelRow;
        const hasValue = lookupModelData?.get(textbox.lookupModelResultField) != null || (textbox.lookupModel == null && !textbox.isEmpty());
        this.toggleCheckStyling(checkImage, hasValue);
    }

    static showSlideout(modelRow?: ModelRow, id?: string, onSaveCallback?: (row: ModelRow) => void, startingStep?: StartingStep): EditRowDecorator {
        const layout = Layout.getLayout("lme/dispatch/AutoMatchProfile") as AutoMatchProfile;
        const row = modelRow?.createBasicCopy();
        const continueButton = this.getContinueButton(layout);
        const saveButton = this.getSaveButton(layout, onSaveCallback);
        const profileId = id ?? modelRow?.get("id");
        const isUpdateAutoMatchProfilesDenied: boolean = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_ADD_UPDATE_AUTOMATCH_PROFILES);
        if (isUpdateAutoMatchProfilesDenied && profileId == null) {
            Snackbar.showSnackbar("You do not have permission to add new Auto Match profiles.");
            return;
        }
        return new EditRowDecorator({
            layout: layout,
            data: row,
            dataSourceMode: DataSourceMode.NONE,
            title: `${profileId != null ? "Edit" : "Add"} Auto Match Profile${profileId != null ? ` - ${profileId}` : ""}`,
            width: "65%",
            height: "100%",
            addlComponents: [continueButton, saveButton],
            layoutLoadListeners: async () => {
                if (row == null && id != null) {
                    await layout.mainDataSource.search({ id: id });
                }
                layout.setAllDataSourcesToMode(profileId != null ? DataSourceMode.UPDATE : DataSourceMode.ADD);
                if (isUpdateAutoMatchProfilesDenied) {
                    const reviewComponents: Component[] = layout.stepReviewAndComplete.components;
                    layout.remove(layout.stepperAutoMatchProfile);
                    layout.add(...reviewComponents);
                    saveButton.visible = false;
                    continueButton.visible = false;
                    return;
                }
                layout.stepperAutoMatchProfile.addBeforeStepListener((event: StepEvent) => {
                    continueButton.visible = event.newStep.id !== layout.stepReviewAndComplete.id;
                    saveButton.visible = event.newStep.id === layout.stepReviewAndComplete.id;
                    if (event.newStep.id === layout.stepReviewAndComplete.id) {
                        // changes to the datasource in other steps do not auto display those changes in separate tables that use the same datasource
                        layout.sourceComments.displayDataInBoundComponents();
                        layout.sourceEquipmentMatch.displayDataInBoundComponents();
                        layout.sourceEquipmentMatchDefault.displayDataInBoundComponents();
                        layout.sourceEquipMatchDetail.displayDataInBoundComponents();

                        // highlight rows that have been appended or edited
                        layout.stepReviewAndComplete.forEveryChildComponent((component) => {
                            if (component instanceof Table) {
                                component.rows.forEach((row: TableRow) => row.data._appending ? row.backgroundColor = "success.lightest" : row.data.hasChanged() ? row.backgroundColor = "primary.lightest" : row.backgroundColor = "");
                            }
                        })
                    }
                });
                layout.startingStep = startingStep;
            }
        });
    }

    private static getContinueButton(layout: AutoMatchProfile): Button {
        return new Button({
            id: "buttonContinue",
            caption: "Continue",
            color: "primary.reverse",
            backgroundColor: "primary",
            width: 140,
            height: 35,
            busyWhenDataSourceBusy: true,
            borderWidth: 1,
            rowBreak: false,
            borderColor: "primary",
            onClick: (event: ClickEvent) => {
                layout.stepperAutoMatchProfile.selectedIndex++;
            }
        });
    }

    private static getSaveButton(layout: AutoMatchProfile, onSaveCallback: (row: ModelRow) => void): Button {
        const button = new Button({
            id: "buttonSave",
            caption: "Save & Close",
            color: "primary.reverse",
            backgroundColor: "primary",
            visible: false,
            width: 140,
            height: 35,
            borderWidth: 1,
            rowBreak: false,
            borderColor: "primary",
            dataSource: layout.mainDataSource,
            busyWhenDataSourceBusy: true,
            onClick: async (event: ClickEvent) => {
                button.busy = true;
                await layout.mainDataSource.post()
                button.busy = false;
                await layout.slideOut()
                onSaveCallback(layout.activeRow);
            }
        });
        return button;
    }

    tableEquipmentMatchOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const textboxAppliesTo = tableRow.findComponentById("textboxAppliesTo") as Textbox;
        const textboxEquipmentTypeId = tableRow.findComponentById("textboxEquipmentTypeId") as Textbox;
        textboxAppliesTo.addChangeListener((event: ChangeEvent) => {
            tableRow.data.set("applies_to", textboxAppliesTo.selectedItem.value);
            textboxEquipmentTypeId.clear();
            this.equipmentTypeAppliesToFilter = textboxAppliesTo.selectedItem.value;
        });
        textboxEquipmentTypeId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.applies_to = this.equipmentTypeAppliesToFilter;
        })
    }

    textboxFieldNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.table_name = "orders";
        event.filter.field_name = "in " + this.ordersLayout.mainDataSource.boundComponents.filter((component) => {
            return this.isConfigurableComponentType(component);
        }).map(comp => comp.field).join(",");
    }

    tableAutoPopulatedFieldsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const textboxFieldName = tableRow.findComponentById("textboxFieldName") as Textbox;
        const panelDefaultValue = tableRow.findComponentById("panelDefaultValue") as Panel;
        this.loadDefaultValue(panelDefaultValue, tableRow);
        textboxFieldName.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            tableRow.data.set("default_value", null);
            await this.loadDefaultValue(panelDefaultValue, tableRow);
        });
        textboxFieldName.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            if (mode === DataSourceMode.SEARCH) {
                return null;
            }
            const result: ValidationResult = {
                isValid: true,
                component: textboxFieldName
            };
            // check if another row has the same field name
            const fieldName = textboxFieldName.getDataValue();
            if (fieldName == null) return;
            const tableRow = TableRow.getContainingTableRow(textboxFieldName);
            this.tableAutoPopulatedFields.rows.forEach((row) => {
                if (!ObjectUtil.deepEqual(row, tableRow) && row.data.get("field_name") === fieldName) {
                    result.isValid = false;
                    result.validationMessage = "Field name must be unique";
                }
            });
            return result;
        };
    }

    private async loadDefaultValue(panelDefaultValue: Panel, tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        if (modelRow.isNull("field_name")) return;
        panelDefaultValue.removeAll();
        const busySpinner = new Label({ color: "primary", fillRow: true });
        busySpinner.busy = true;
        panelDefaultValue.add(busySpinner)
        const component = this.ordersLayout.findComponentByField(modelRow.get("field_name")).find(comp => this.isConfigurableComponentType(comp));
        const generatedComponent: ConfigurableComponent = await this.generateComponentFromField(component, modelRow);
        generatedComponent.printable = tableRow.rowIsPrintable();
        panelDefaultValue.remove(busySpinner);
        panelDefaultValue.add(generatedComponent);
        panelDefaultValue.displayData(modelRow, null, tableRow.index);
        this.alignComponentLeft(generatedComponent);
    }

    private async generateComponentFromField(orderLayoutComponent: Component, modelRow: ModelRow): Promise<ConfigurableComponent> {
        let component: ConfigurableComponent = new Textbox({
            id: "textboxDefaultValue",
            captionVisible: false,
            fillRow: true,
            field: "default_value",
            boundRow: modelRow
        });
        if (orderLayoutComponent instanceof Switch) {
            component = new Switch({
                id: "switchDefaultValue",
                captionVisible: false,
                field: "default_value",
                boundRow: modelRow,
                leftCaption: "No",
                rightCaption: "Yes",
                leftValue: "N",
                rightValue: "Y"
            });
        } else if (orderLayoutComponent instanceof Checkbox) {
            component = new Checkbox({
                id: "checkboxDefaultValue",
                field: "default_value",
                boundRow: modelRow,
                valueChecked: "Y",
                valueUnchecked: "N",
                caption: orderLayoutComponent.caption
            });
            component.addPrintableListener((event: PrintableEvent) => {
                component.caption = component.printable ? "" : orderLayoutComponent.caption;
                component.padding = component.printable ? 0 : 4;
            });
        } else if (orderLayoutComponent instanceof Textbox) {
            component.displayType = orderLayoutComponent.displayType;
            component["_boundField"] = orderLayoutComponent["_boundField"];
            if (orderLayoutComponent.lookupModel != null) {
                component.lookupModel = orderLayoutComponent.lookupModel;
                component.lookupModelLayout = orderLayoutComponent.lookupModelLayout;
                component.lookupModelDisplayField = orderLayoutComponent.lookupModelDisplayField;
                component.lookupModelResultField = orderLayoutComponent.lookupModelResultField
                component.quickInfoLayout = orderLayoutComponent.quickInfoLayout;
                const beforeLookupModelSearchListener = orderLayoutComponent.getListeners("_lookupListeners")?.listeners?.[0];
                if (beforeLookupModelSearchListener != null) {
                    component.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
                        try {
                            beforeLookupModelSearchListener(event);
                        } catch (error) {
                            // do nothing, the listener borrowed from the Orders layout may have specific business logic that is not applicable here
                        }
                    });
                }
                component.lookupModelMaxResults = 200;
                component.lookupModelMinChars = orderLayoutComponent.lookupModelMinChars;
                component.lookupModelAllowFreeform = orderLayoutComponent.lookupModelAllowFreeform;
                if (component.lookupModelLayout != null && !modelRow.isNull("default_value")) {
                    await AutoMatchProfile.applyLookupModelData(component, modelRow);
                }
            } else if (orderLayoutComponent.items?.length > 0) {
                component.items = orderLayoutComponent.items;
            }
        } else if (orderLayoutComponent instanceof Location) {
            component = new Location({
                id: "locationDefaultValue",
                captionVisible: false,
                fillRow: true,
                field: "default_value",
                boundRow: modelRow,
                locationIdField: "default_value",
                cityIdField: orderLayoutComponent.cityIdField,
                cityField: orderLayoutComponent.cityField,
                stateField: orderLayoutComponent.stateField,
                zipField: orderLayoutComponent.zipField,
                quickInfoLayout: orderLayoutComponent.quickInfoLayout
            });
            component.locationNameField = orderLayoutComponent.locationNameField;
            component.addressField = orderLayoutComponent.addressField;
            if (!modelRow.isNull("default_value")) {
                await AutoMatchProfile.applyLookupForLocation(component, modelRow);
            }
        }
        return component;
    }

    public static applyLookupModelData(textbox: Textbox, modelRow: ModelRow): Promise<void> {
        return new Promise((resolve) => {
            const lookupLayout = Layout.getLayout(textbox.lookupModelLayout);
            lookupLayout.addLayoutLoadListener(() => {
                const filter = { [textbox.lookupModelResultField]: modelRow.get(textbox.field) };
                lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then(response => {
                    if (response?.modelRows?.length === 1) {
                        modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field) , response.modelRows[0]);
                    } else {
                        const lookupModelData = new ModelRow(modelRow._modelPath, false);
                        lookupModelData.set(textbox.lookupModelResultField, modelRow.get(textbox.field));
                        lookupModelData.set(textbox.lookupModelDisplayField, modelRow.get(textbox.field));
                        modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), lookupModelData);
                    }
                    resolve();
                });
            })
        });
    }

    public static applyLookupForLocation(location: Location, modelRow: ModelRow): Promise<void> {
        return new Promise((resolve) => {
            if (modelRow == null) {
                resolve();
                return;
            }
            const lookupLayout = Layout.getLayout("lme/dispatch/LocationLookup");
            lookupLayout.addLayoutLoadListener(() => {
                const filter = { id: modelRow.get(location.field) };
                lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then(response => {
                    const lookupModelData = response.modelRows[0];
                    modelRow.set(location.locationIdField, lookupModelData.get("id"));
                    modelRow.set(location.locationNameField, lookupModelData.get("name"));
                    modelRow.set(location.cityIdField, lookupModelData.get("city_id"));
                    modelRow.set(location.cityField, lookupModelData.get("city_name"));
                    modelRow.set(location.stateField, lookupModelData.get("state"));
                    modelRow.set(location.zipField, lookupModelData.get("zip"));
                    modelRow.set(location.addressField, lookupModelData.get("address1"));
                    modelRow.setLookupModelData(location.field, lookupModelData);

                    // reset location fields in original data to prevent the datasource from being marked as changed
                    modelRow.resetOriginalData(location.locationNameField);
                    modelRow.resetOriginalData(location.cityIdField);
                    modelRow.resetOriginalData(location.cityField);
                    modelRow.resetOriginalData(location.stateField);
                    modelRow.resetOriginalData(location.zipField);
                    modelRow.resetOriginalData(location.addressField);

                    resolve();
                });
            })
        });
    }

    tableAutoPopulatedFieldsSummaryOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const panelDefaultValueSummary = tableRow.findComponentById("panelDefaultValueSummary") as Panel;
        if (panelDefaultValueSummary != null) {
            this.loadDefaultValue(panelDefaultValueSummary, tableRow);
        }
    }

    textboxQuantityOnPrintable(event: PrintableEvent) {
        const textboxQuantity = event.target as Textbox;
        textboxQuantity.fillRow = true;
    }

    tableRequiredEquipmentOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data as ModelRow;
        modelRow.set("equipment_match_id", this.sourceEquipmentMatch.activeRow.get("id"));
        modelRow.set("is_shipper_consignee", "B"); // default to both, will be removed when the column is added to the table
    }

    textboxOrderTypeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    textboxTrailerMatchBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.applies_to = "L";
    }

    textboxCustomerIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
        event.filter.type_of = "C";
    }

    private isConfigurableComponentType(component: Component): boolean {
        return component instanceof Textbox || component instanceof Switch || component instanceof Checkbox || component instanceof Location;
    }

    // janky code to force numeric display types to align left
    // even when the textbox's align is set left, its inner Label component stays right
    private alignComponentLeft(component: Component) {
        if (component instanceof Textbox) {
            const element = component._element.getElementsByClassName("lbl-base")?.item(0);
            if (element instanceof HTMLElement) {
                element.style.textAlign = "left";
            }
        }
    }

    tableCommentsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const switchCopyToOrder = tableRow.findComponentById("switchCopyToOrder") as Switch;
        const attachmentUpload = tableRow.findComponentById("attachmentUpload") as Attachment;
        attachmentUpload.visible = !ConversionUtil.parseBoolean(switchCopyToOrder.getDataValue());
        switchCopyToOrder.addChangeListener((event: ChangeEvent) => {
            attachmentUpload.visible = !ConversionUtil.parseBoolean(switchCopyToOrder.getDataValue());
            if (switchCopyToOrder.getDataValue() === "N") {
                attachmentUpload.displayData(null, null, tableRow.index);
            }
        });
    }
}
